import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import {
  ArrowChevronRight,
  ArrowLineOnlyLeft,
  Chat,
  Document,
  DownloadDocument,
  Heart,
} from '@swyftx/aviary/icons/outlined';
import { MenuItem } from '@swyftx/aviary/molecules/Menu';

import { cn } from '@shared/utils/lib/ui';

import { useLogout } from '@hooks/Logout';
import { MarginLegalInformationModal, MarginTransactionsReportModal } from '@routes/SwyftxMargin/components/Account';

import { observer } from 'mobx-react-lite';
import { useIntercom } from 'react-use-intercom';
import { SwitchPlatformMenuItem } from 'src/lib/user-profile/components/SwitchPlatformMenuItem/SwitchPlatformMenuItem';

import { SwyftxMarginAppHeaderProfileDetails } from './SwyftxMarginAppHeaderProfileDetails';
import { SwyftxThemeSelector } from '../../SwyftxThemeSelector';

type Props = {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (mobileMenuOpen: boolean) => void;
};

const SwyftxMarginAppShellMobileMenu: React.FC<Props> = observer(({ mobileMenuOpen, setMobileMenuOpen }) => {
  const { t } = useTranslation('margin');
  const { show } = useIntercom();
  const logout = useLogout();

  return (
    <div
      className={cn(
        'block min-w-[350px] overflow-y-scroll bg-color-background-surface-primary p-16 transition-[transform] duration-300',
        mobileMenuOpen ? 'translate-x-0 sm:translate-x-[-350px]' : 'translate-x-[-350px]',
      )}
    >
      <FlexLayout className='h-full w-full' direction='column' justifyContent='space-between' spacing={16}>
        <FlexLayout direction='column' spacing={8}>
          <FlexLayout alignItems='center' justifyContent='space-between'>
            <div onClick={() => setMobileMenuOpen(false)}>
              <ArrowLineOnlyLeft className='cursor-pointer text-color-text-primary' />
            </div>
          </FlexLayout>
          <SwyftxMarginAppHeaderProfileDetails showAvatar />
          <SwitchPlatformMenuItem />
          <Separator />
          <FlexLayout direction='column' spacing={4}>
            <SwyftxThemeSelector spotlightElementId='settings-menu-theme' size='small' />
          </FlexLayout>
          <Separator />
          <FlexLayout direction='column' spacing={4}>
            <MarginTransactionsReportModal>
              <span>
                <MenuItem
                  icon={<DownloadDocument width={24} height={24} className='h-24 w-24 text-color-text-primary' />}
                  text={t('transactionReports.title')}
                />
              </span>
            </MarginTransactionsReportModal>

            <MenuItem
              icon={<Chat width={24} height={24} className='h-24 w-24 text-color-text-primary' />}
              text={t('profile.labels.liveChat')}
              action={show}
              preventCloseOnSelect
            />

            <MenuItem
              icon={<Heart width={24} height={24} className='h-24 w-24 text-color-text-primary' />}
              text={t('profile.labels.provideFeedback')}
              preventCloseOnSelect
            />
            <MarginLegalInformationModal>
              <MenuItem
                icon={<Document width={24} height={24} className='h-24 w-24 text-color-text-primary' />}
                text={t('profile.legal.legalInformation')}
                endAdornment={<ArrowChevronRight className='h-24 w-24 text-color-text-primary' />}
              />
            </MarginLegalInformationModal>
          </FlexLayout>
        </FlexLayout>

        <FlexLayout direction='column' spacing={16}>
          <Button variant='filled' size='lg' onClick={logout}>
            {t('profile.buttons.signOut')}
          </Button>
        </FlexLayout>
      </FlexLayout>
    </div>
  );
});

export { SwyftxMarginAppShellMobileMenu };
